/**
 * On-device read-only snapshots for offline continuity.
 *
 * Every catalogue read goes through `/api`, which the worker deliberately
 * never caches — so a buyer who viewed a part yesterday used to see nothing
 * but the offline card today. We keep a small, clearly-labelled copy of the
 * last products (and the last result set) the buyer actually opened.
 *
 * Rules that keep this honest:
 *   - always rendered as a saved copy with the time it was taken,
 *   - never used for checkout pricing or stock decisions,
 *   - replaced by a live read as soon as the network is back.
 *
 * Order snapshots keep living in `order-snapshot.ts`; they are re-exported
 * here so callers have one import for "what do we still know offline".
 */

export {
  saveOrderSnapshot,
  readOrderSnapshot,
  readOrderSnapshots,
  savedAgo,
  type OrderSnapshot,
} from "./order-snapshot";

export type ProductSnapshot = {
  slug: string;
  title: string;
  titleBn?: string | null;
  price?: number | null;
  mrp?: number | null;
  image?: string | null;
  shop?: string | null;
  inStock?: boolean | null;
  /** When this copy was taken (epoch ms). */
  savedAt: number;
};

export type ResultSetSnapshot = {
  /** Route + query this list came from, e.g. `/search?q=brake+pad`. */
  key: string;
  label: string;
  slugs: string[];
  savedAt: number;
};

const PRODUCT_KEY = "jb.product.snapshots";
const RESULTS_KEY = "jb.results.snapshot";
const PRODUCT_MAX = 20;

function readJson<T>(key: string, fallback: T): T {
  if (typeof window === "undefined") return fallback;
  try {
    const raw = window.localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : fallback;
  } catch {
    return fallback;
  }
}

function writeJson(key: string, value: unknown): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(key, JSON.stringify(value));
  } catch {
    /* private mode / quota — snapshots are a nicety, not a requirement */
  }
}

function readProducts(): ProductSnapshot[] {
  const list = readJson<ProductSnapshot[]>(PRODUCT_KEY, []);
  return Array.isArray(list) ? list.filter((p) => p && typeof p.slug === "string") : [];
}

export function saveProductSnapshot(snapshot: Omit<ProductSnapshot, "savedAt">): void {
  if (typeof window === "undefined" || !snapshot?.slug) return;
  const next = [
    { ...snapshot, savedAt: Date.now() },
    ...readProducts().filter((p) => p.slug !== snapshot.slug),
  ].slice(0, PRODUCT_MAX);
  writeJson(PRODUCT_KEY, next);
}

export function readProductSnapshot(slug: string): ProductSnapshot | null {
  return readProducts().find((p) => p.slug === slug) ?? null;
}

export function readProductSnapshots(): ProductSnapshot[] {
  return readProducts();
}

/** Resolve a saved product from a pathname such as `/products/brake-pad-x`. */
export function snapshotForPath(pathname: string): ProductSnapshot | null {
  const match = /^\/products\/([^/?#]+)/.exec(pathname);
  if (!match) return null;
  try {
    return readProductSnapshot(decodeURIComponent(match[1]!));
  } catch {
    return readProductSnapshot(match[1]!);
  }
}

export function saveResultSetSnapshot(snapshot: Omit<ResultSetSnapshot, "savedAt">): void {
  if (typeof window === "undefined" || !snapshot?.key) return;
  writeJson(RESULTS_KEY, { ...snapshot, savedAt: Date.now() });
}

export function readResultSetSnapshot(): ResultSetSnapshot | null {
  const snap = readJson<ResultSetSnapshot | null>(RESULTS_KEY, null);
  return snap && Array.isArray(snap.slugs) ? snap : null;
}
